import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Fingerprint, ShieldCheck, BarChart3, FileSpreadsheet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-emerald-900 flex items-center justify-center">
              <Fingerprint className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-emerald-950">راصد حضوري</h1>
              <p className="text-xs text-slate-500">نظام تدقيق وتحليل الحضور الذكي</p>
            </div>
          </div>
          <h2 className="text-lg font-bold mb-1">تسجيل الدخول</h2>
          <p className="text-sm text-slate-500 mb-6">أدخل بياناتك للوصول إلى لوحة التقارير</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">البريد الإلكتروني</Label>
              <Input id="email" type="email" dir="ltr" data-testid="login-email-input" value={email}
                onChange={(e) => setEmail(e.target.value)} required placeholder="name@example.com" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">كلمة المرور</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link"
                  className="text-xs text-emerald-800 hover:underline">نسيت كلمة المرور؟</Link>
              </div>
              <Input id="password" type="password" dir="ltr" data-testid="login-password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
            </div>
            {error && (
              <div data-testid="login-error-alert"
                className="text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <Button type="submit" data-testid="login-submit-button" disabled={loading}
              className="w-full bg-emerald-900 hover:bg-emerald-950 text-white h-11">
              {loading ? "جارٍ المعالجة..." : "دخول"}
            </Button>
          </form>
          <p className="mt-4 text-sm text-slate-500">
            ليس لديك اشتراك؟{" "}
            <Link to="/subscribe" data-testid="go-subscribe-link" className="text-emerald-800 font-semibold hover:underline">
              اشترك الآن
            </Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:flex w-[42%] bg-emerald-950 text-white flex-col justify-between p-12">
        <div />
        <div className="space-y-8">
          <h3 className="text-3xl font-extrabold leading-snug">
            منصّة متكاملة لسحب حضور الموظفين من حضوري وإصدار تقارير الدوام والمسائلات
          </h3>
          <div className="space-y-4">
            {[
              { icon: ShieldCheck, text: "تقارير مسائلات رسمية جاهزة للطباعة بترويسة الوزارة والمدرسة" },
              { icon: BarChart3, text: "تحليل دقائق التأخير والخروج المبكر ومؤشرات الالتزام" },
              { icon: FileSpreadsheet, text: "تصدير Excel و PDF بترويسة رسمية قابلة للتخصيص" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-900 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-amber-400" />
                </div>
                <span className="text-sm text-emerald-50">{text}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-emerald-300/60">يعمل بالتكامل مع إضافة كروم لسحب البيانات تلقائياً</p>
      </div>
    </div>
  );
}
